import React from 'react';
import { ConfigInterface, responseInterface } from 'swr';
import { api, useApi, parseUrl } from '@gik/core/api';
import { ICalendarEvent, ICalendarEntry } from '@gik/calendar/models/Calendar';

export interface ICalendarEntriesResponse {
  events: ICalendarEvent[];
  entries: ICalendarEntry[];
}

const path = 'inkinds';

function getParams(fromDate: string, toDate: string) {
  return {
    fromDate,
    toDate,
  };
}

export function getCalendarEvents(inkindRouteId: string, fromDate: string, toDate: string) {
  const valid = fromDate !== 'Invalid date' && toDate !== 'Invalid date';
  if (!valid) return Promise.resolve(null);

  const finalUrl = parseUrl(`${path}/${inkindRouteId}/calendar`, {
    searchParams: getParams(fromDate, toDate),
  });
  return api.get(finalUrl);
}

export function useCalendarEvents(inkindRouteId: string, fromDate: string, toDate: string, config?: ConfigInterface) {
  const valid = fromDate !== 'Invalid date' && toDate !== 'Invalid date';

  return useApi<ICalendarEntriesResponse>(valid ? `${path}/${inkindRouteId}/calendar` : null, config, {
    searchParams: getParams(fromDate, toDate),
  });
}

export function useAvailableCalendarEvents(
  inkindRouteId: string,
  calendarEventTypeId: number,
  config?: ConfigInterface
) {
  return useApi<ICalendarEntriesResponse>(
    inkindRouteId && calendarEventTypeId !== undefined ? `${path}/${inkindRouteId}/calendar` : null,
    config,
    {
      searchParams: {
        availableOnly: true,
        calendarEventTypeId,
      },
    }
  );
}

/**
 * Used for testing
 * This function changed the data before returning it.
 */
export function useCalendarEventsTest(
  inkindRouteId: string,
  fromDate: string,
  toDate: string,
  config?: ConfigInterface
): responseInterface<ICalendarEntriesResponse, Error> {
  const response = useCalendarEvents(inkindRouteId, fromDate, toDate, config);

  const [data, setData] = React.useState<ICalendarEntriesResponse>(null);

  /**
   * Create a list of matching service categories when all data is ready
   */
  React.useEffect(() => {
    if (!response.data) return;

    const newData = JSON.parse(JSON.stringify(response.data));

    // get services for the requested event type
    newData.entries?.forEach(item => {
      item.allowedGiftCardIds = [];
      return item;
    });

    setData(newData);
  }, [response.data]);

  return {
    error: response.error,
    isValidating: response.isValidating,
    revalidate: response.revalidate,
    mutate: response.mutate,
    data,
  };
}
